import { createContext, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, ChevronDown } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { PATHS, TOURS, useLang, useT } from "../../lib/i18n";
import { applyConsent, getConsent, initTracking } from "../../lib/tracking";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

export const AltCtx = createContext({ alts: null, setAlts: () => {} });
export const useAlts = () => useContext(AltCtx);

export const Logo = () => (
  <span className="flex items-center gap-2 font-display text-2xl font-extrabold tracking-tight" data-testid="site-logo">
    <span className="relative inline-block h-6 w-6 rounded-full bg-[var(--ball)] shadow-[0_0_24px_rgba(215,255,58,.45)]">
      <span className="absolute inset-0 rounded-full border-2 border-[#07090c]/40 [clip-path:inset(0_50%_0_0)] scale-x-75" />
    </span>
    <span>Tennis<span className="text-[var(--ball)]">Tipps</span></span>
  </span>
);

function LangSwitch() {
  const lang = useLang();
  const { alts } = useAlts();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const go = (l) => {
    if (l === lang) return;
    const alt = alts?.[l];
    nav(alt ? new URL(alt).pathname : PATHS[l].home);
    if (!alt && pathname === "/") nav(PATHS[l].home);
  };
  return (
    <div className="flex rounded-full border border-border p-0.5 text-xs font-semibold" data-testid="lang-switch">
      {["de", "en"].map((l) => (
        <button key={l} onClick={() => go(l)} data-testid={`lang-${l}`}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors ${l === lang ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
      ))}
    </div>
  );
}

export function Header() {
  const lang = useLang();
  const t = useT();
  const P = PATHS[lang];
  const { user, config } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);
  const links = [[P.tips, t("Tennis Tipps", "Tennis Tips")], [P.today, t("Heute", "Today")], [P.guides, t("Ratgeber", "Guides")], ...(config?.stats_public ? [[P.record, t("Bilanz", "Record")]] : []), [P.news, "News"]];
  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background/75 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-6">
        <Link to={P.home} data-testid="nav-home"><Logo /></Link>
        <nav className="hidden items-center gap-1 lg:flex">
          {links.map(([to, l]) => <Link key={to} to={to} className="rounded-full px-3 py-2 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid={`nav-${to.split("/")[2] || "home"}`}>{l}</Link>)}
          <div className="group relative">
            <button className="flex items-center gap-1 rounded-full px-3 py-2 text-sm text-muted-foreground hover:text-foreground" data-testid="nav-tours">{t("Turniere", "Tournaments")}<ChevronDown size={14} /></button>
            <div className="invisible absolute left-0 top-full w-48 rounded-xl border border-border bg-popover p-2 opacity-0 transition-opacity group-hover:visible group-hover:opacity-100">
              {TOURS.map(([s, n]) => <Link key={s} to={`/${lang}/${s}/`} className="block rounded-lg px-3 py-2 text-sm hover:bg-secondary" data-testid={`nav-tour-${s}`}>{n}</Link>)}
            </div>
          </div>
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <LangSwitch />
          {user ? (
            <Link to={user.role === "admin" && pathname.startsWith("/admin") ? P.account : P.account} data-testid="nav-account"><Button variant="secondary" size="sm" className="rounded-full"><User size={14} className="mr-1" />{t("Konto", "Account")}</Button></Link>
          ) : (
            <Link to={P.login} className="hidden sm:block" data-testid="nav-login"><Button variant="ghost" size="sm" className="rounded-full">{t("Anmelden", "Login")}</Button></Link>
          )}
          <Link to={P.vip} className="hidden sm:block" data-testid="nav-vip"><Button size="sm" variant={user ? "default" : "ghost"} className="rounded-full font-semibold">VIP</Button></Link>
          {!user && <Link to={P.register} className="hidden sm:block" data-testid="nav-register"><Button size="sm" className="rounded-full font-semibold">{t("Kostenlos registrieren", "Join free")}</Button></Link>}
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="menu">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="border-t border-border bg-background px-4 py-4 lg:hidden" data-testid="nav-mobile-menu">
          {[...links, ...TOURS.map(([s, n]) => [`/${lang}/${s}/`, n]), [P.vip, "VIP"], [user ? P.account : P.login, user ? t("Konto", "Account") : t("Anmelden", "Login")]].map(([to, l]) => (
            <Link key={to + l} to={to} className="block py-2 text-base">{l}</Link>
          ))}
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const lang = useLang();
  const t = useT();
  const P = PATHS[lang];
  const { config, user } = useAuth();
  const { pathname } = useLocation();
  const sticky = user === null ? false : !user && !pathname.startsWith("/admin") && !/\/(register|login|registrieren|anmelden)/.test(pathname);
  return (
    <footer className={`relative z-10 mt-24 border-t border-border bg-card/40 ${sticky ? "pb-20 sm:pb-0" : ""}`}>
      {sticky && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/90 p-3 backdrop-blur-xl sm:hidden" data-testid="sticky-register-bar">
          <Link to={P.register} data-testid="sticky-register-btn"><Button className="w-full rounded-full font-semibold">{t("Kostenlos registrieren & weitere Tipps sehen", "Join free & unlock more tips")}</Button></Link>
        </div>
      )}
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-4">
        <div className="md:col-span-2">
          <Logo />
          <p className="mt-4 max-w-md text-sm text-muted-foreground">{t("Geprüfte Tennis Wetttipps mit Quote, Einsatz und Begründung. Keine Wettannahme – wir liefern Analysen.", "Reviewed tennis betting tips with odds, stake and reasoning. We do not accept bets – we provide analysis.")}</p>
          <div className="mt-6 flex items-center gap-3 rounded-xl border border-[var(--loss)]/30 bg-[var(--loss)]/5 p-4 text-xs text-muted-foreground" data-testid="footer-responsible">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border-2 border-[var(--loss)] font-display text-base font-bold text-[var(--loss)]">18+</span>
            {t("Glücksspiel kann süchtig machen. Spiele verantwortungsvoll. Hilfe: BZgA 0800 1 37 27 00 · check-dein-spiel.de", "Gambling can be addictive. Please play responsibly. Help: begambleaware.org")}
          </div>
        </div>
        <div className="space-y-2 text-sm">
          <p className="font-display text-lg font-bold">{t("Tipps", "Tips")}</p>
          {[[P.tips, t("Tennis Wetttipps", "Tennis Betting Tips")], [P.today, t("Wetttipps heute", "Predictions Today")], [P.sport, t("Sportwetten Tipps", "Sports Betting Tips")], [P.tennisSport, t("Tennis Sportwetten Tipps", "Tennis Tips")], [P.predictions, t("Tennis Prognosen", "Tennis Predictions")], [P.live, t("Tennis Live Wetten Tipps", "Live Betting Tips")], [P.guides, t("Ratgeber", "Guides")], ...(config?.stats_public ? [[P.record, t("Bilanz", "Record")]] : []), [P.vip, "VIP"]].map(([to, l]) => <Link key={to} to={to} className="block text-muted-foreground hover:text-foreground">{l}</Link>)}
        </div>
        <div className="space-y-2 text-sm">
          <p className="font-display text-lg font-bold">{t("Rechtliches", "Legal")}</p>
          {[[P.imprint, t("Impressum", "Imprint")], [P.privacy, t("Datenschutz", "Privacy")], [P.terms, t("AGB", "Terms")], [P.responsible, t("Verantwortungsvolles Spielen", "Responsible Gambling")]].map(([to, l]) => <Link key={to} to={to} className="block text-muted-foreground hover:text-foreground" data-testid={`footer-${to.split("/")[2]}`}>{l}</Link>)}
          <button onClick={() => window.dispatchEvent(new Event("tt-consent-open"))} className="block text-muted-foreground hover:text-foreground" data-testid="footer-cookie-settings">{t("Cookie-Einstellungen", "Cookie settings")}</button>
        </div>
      </div>
      <p className="border-t border-border py-5 text-center text-xs text-muted-foreground">© {new Date().getFullYear()} {config?.company?.name || "TennisTipps LLC"} · 18+</p>
    </footer>
  );
}

export function CookieBanner() {
  const t = useT();
  const { config } = useAuth();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [c, setC] = useState({ analytics: false, marketing: false });
  useEffect(() => {
    if (!config) return;
    initTracking(config.tracking);
    if (!getConsent()) setOpen(true);
    const h = () => { setC(getConsent() || { analytics: false, marketing: false }); setCustom(true); setOpen(true); };
    window.addEventListener("tt-consent-open", h);
    return () => window.removeEventListener("tt-consent-open", h);
  }, [config]);
  if (!open) return null;
  const save = (v) => { applyConsent(v); setOpen(false); };
  return (
    <div className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-2xl rounded-2xl border border-border bg-popover/95 p-5 shadow-2xl backdrop-blur-xl sm:inset-x-6" data-testid="cookie-banner">
      <p className="font-display text-xl font-bold">Cookies</p>
      <p className="mt-1 text-sm text-muted-foreground">{t("Wir nutzen Cookies für Statistik (Google Analytics/Tag Manager) und Marketing (Meta Pixel) nur mit deiner Einwilligung. Notwendige Cookies sind immer aktiv.", "We use cookies for analytics (Google Analytics/Tag Manager) and marketing (Meta Pixel) only with your consent. Necessary cookies are always active.")}</p>
      {custom && (
        <div className="mt-4 space-y-3 text-sm">
          {[["analytics", t("Statistik", "Analytics")], ["marketing", "Marketing"]].map(([k, l]) => (
            <label key={k} className="flex items-center justify-between"><span>{l}</span><Switch checked={c[k]} onCheckedChange={(v) => setC({ ...c, [k]: v })} data-testid={`cookie-toggle-${k}`} /></label>
          ))}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button className="rounded-full" onClick={() => save({ analytics: true, marketing: true })} data-testid="cookie-accept-all">{t("Alle akzeptieren", "Accept all")}</Button>
        <Button variant="secondary" className="rounded-full" onClick={() => save({ analytics: false, marketing: false })} data-testid="cookie-reject">{t("Nur notwendige", "Necessary only")}</Button>
        {custom ? <Button variant="ghost" className="rounded-full" onClick={() => save(c)} data-testid="cookie-save">{t("Auswahl speichern", "Save selection")}</Button>
          : <Button variant="ghost" className="rounded-full" onClick={() => setCustom(true)} data-testid="cookie-customize">{t("Einstellungen", "Settings")}</Button>}
      </div>
    </div>
  );
}
